import { getClient } from '../core/global-config.js';
import { createAndWaitTask } from '../core/index.js';

export interface YouchuanEditParams {
  /**
   * 任务ID
   */
  jobId?: string;
  /**
   * 图像编号，必须是0、1、2或3
   */
  imageNo?: 0 | 1 | 2 | 3;
  /**
   * 重混提示词，长度1-8192字符
   */
  remixPrompt?: string;
  /**
   * 画布参数
   */
  canvas?: object;
  /**
   * 图像位置参数
   */
  imgPos?: object;
  /**
   * 回调URL，必须是有效的HTTP/HTTPS URL
   */
  callback?: string;
  /**
   * 遮罩参数
   */
  mask?: object;
}

export interface YouchuanEditResource {
  type: string;
  url: string;
  size?: number;
  jobId?: string;
  [key: string]: any;
}

/**
 * youchuan_edit
 *
 * Youchuan Edit Image Generator
 *
 * Image editing implementation for Youchuan Open Platform (悠船开放平台).
 *
 * API Documentation: https://tob.youchuan.cn/openapi/apis/%E5%9B%BE%E7%89%87%E7%94%9F%E6%88%90/api_edit
 *
 * @example
 * ```typescript
 * import { initSeacloud, youchuanEdit } from 'seacloud-sdk';
 *
 * initSeacloud('your-api-key');
 *
 * const result = await youchuanEdit({
 *   jobId: 'your-job-id',
 *   imageNo: 0,
 *   remixPrompt: 'Make the sky more blue'
 * });
 *
 * console.log(result); // 返回生成的资源列表
 * ```
 *
 * @param params 请求参数，详见 YouchuanEditParams 接口定义
 * @returns 生成的资源列表
 */
export async function youchuanEdit(
  params: YouchuanEditParams,
): Promise<YouchuanEditResource[]> {
  const client = await getClient();

  const pollingOptions = {
    intervalMs: 3000,
    maxAttempts: 60,
  };

  const result = await createAndWaitTask(
    client,
    '/model/v1/generation',
    {
      model: 'youchuan_edit',
      input: [{ params }],
    },
    pollingOptions,
  );

  const resources: YouchuanEditResource[] = [];

  if (result.output) {
    for (const item of result.output) {
      if (item.content) {
        for (const resource of item.content) {
          resources.push({
            type: resource.type || 'unknown',
            url: resource.url || '',
            size: resource.size,
            jobId: resource.jobId,
            ...resource,
          });
        }
      }
    }
  }

  return resources;
}
